import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription, Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { DocumentManagementService } from "../../../common/services/document-management.service";
import { OnBaseQueryModel, OnBaseKeyword, OnBaseKeywordQuery, QueryResult, KeywordRelation } from "../../../common/models/onBaseIntegrationAndMapping.model";
import { SortAttribute, SortArgs, SortDirection } from "../../../core/models/sort.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { PageData } from "../../../common/models/paging.model";

@Component({
    selector: 'onbase-well-documents',
    templateUrl: './onbase-well-documents.component.html',
    styleUrls: ['./onbase-well-documents.component.scss'],
    standalone: false
})
export class OnBaseWellDocumentsComponent implements OnInit, OnDestroy {

    apiKeyword: OnBaseKeyword;

    @Input() apiNumber: string;
    documents: QueryResult;
    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);
    private _defaultLoadingMessage: string = "Searching for documents.. please wait";
    private _loadingMessage: string;
    get loadingMessage(): string {
        return this._loadingMessage;
    }

    query: OnBaseQueryModel;

    requiredWords: OnBaseKeywordQuery[];

    showLoader: boolean = false;

    subscription: Subscription = new Subscription();

    constructor(private readonly _docService: DocumentManagementService, public readonly dialogService: DynamicDialogService) { }

    ngOnInit() {
        this.query = {
            documentTypeIds: [],
            documentTypeNames: [],
            matchAll: true,
            maxResults: 50,
            resultsPerPage: 25,
            pageNumber: 1,
            displayKeywordTypes: [],
            searchParameters: [],
            sort: SortAttribute.DocumentID,
            isAscending: true
        } as OnBaseQueryModel;

        const word$ = this.findAPIKeyword().pipe(tap((word : OnBaseKeyword) => {
            const apiWord = new OnBaseKeywordQuery(word.id, word.name, this.apiNumber, "eq");
            apiWord.relation = KeywordRelation.And;
            this.requiredWords = [apiWord];
        })).subscribe(
            () => { },
            (error) => { this.handleError(error); }
        );
        this.subscription.add(word$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    findAPIKeyword(): Observable<OnBaseKeyword> {
        let obs$: Observable<OnBaseKeyword>;
        if (this.apiKeyword != null) {
            obs$ = of(this.apiKeyword);
        } else {
            obs$ = this._docService.getAllKeywords().pipe(
                map((data: OnBaseKeyword[]) => {
                    this.apiKeyword = data.find(k => k.name.toLocaleLowerCase().includes("api id number"));
                    return this.apiKeyword;
                }));
        }
        return obs$;
    }

    handleError(error: any): void {
        console.error(error);
        this.toggleLoader();
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    onPageChanged(data: PageData): void {
        const newQuery: OnBaseQueryModel = {
            documentTypeIds: this.query.documentTypeIds,
            documentTypeNames: this.query.documentTypeNames,
            matchAll: this.query.matchAll,
            maxResults: data.resultsPerPage * 5,
            resultsPerPage: data.resultsPerPage,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            sort: this.query.sort,
            isAscending: this.query.isAscending,
            searchParameters: this.query.searchParameters
        } as OnBaseQueryModel;
        this._loadingMessage = `Retrieving page ${data.pageNumber} at ${data.resultsPerPage} results per page.. please wait`;
        this.runQuery(newQuery);
    }

    onSorting(data: SortArgs): void {
        let sort: SortAttribute = SortAttribute.DocumentID;
        switch (data.sortProperty.toLocaleLowerCase()) {
            case "document name":
                sort = SortAttribute.DocumentName;
                break;
            case "document type":
                sort = SortAttribute.DocumentTypeName;
                break;
            default:
                sort = SortAttribute.DocumentID;
                break;
        }
        const newQuery: OnBaseQueryModel = {
            documentTypeIds: this.query.documentTypeIds,
            documentTypeNames: this.query.documentTypeNames,
            matchAll: this.query.matchAll,
            maxResults: this.query.maxResults,
            resultsPerPage: this.query.resultsPerPage,
            displayKeywordTypes: this.query.displayKeywordTypes,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            pageNumber: 1,
            searchParameters: this.query.searchParameters,
            isAscending: data.sortDirection === SortDirection.Ascending,
            sort: sort
        } as OnBaseQueryModel;
        this._loadingMessage = "Resorting.. please wait";
        this.runQuery(newQuery);
    }

    runQuery(model: OnBaseQueryModel): void {
        if (model != null) {
            this.query = model;
            if (this.loadingMessage == null || this.loadingMessage.length === 0) {
                this._loadingMessage = this._defaultLoadingMessage;
            }
            this.toggleLoader();
            const data$ = this._docService.query(model).subscribe(
                (data: QueryResult) => {
                    this.documents = data;
                    this.toggleLoader();
                    this._loadingMessage = "";
                },
                (error) => {
                    this._loadingMessage = "";
                    this.handleError(error);                    
                }
            );
            this.subscription.add(data$);
        }
    }

    

    toggleLoader(): void {        
        this.showLoader = !this.showLoader;
    }
}
